import { CombinedGraphQLErrors } from "@apollo/client/errors";
import { useMutation } from "@apollo/client/react";
import { useAuth, useCurrentUser, useTeam } from "@hooks";
import mixpanel from "mixpanel-browser";
import { useCallback } from "react";

import { readCachedConsentStatus } from "./consentStatusCache";
import { LOG_RECORDING_CONSENT } from "../../graphql/mutations/RecordingConsent";

export function useRecordingConsent() {
  const { user, loading: userLoading } = useCurrentUser();
  const { userUuid } = useAuth();
  const { currentTeam } = useTeam();
  const [logConsent, { loading: isLoggingConsent }] = useMutation(
    LOG_RECORDING_CONSENT,
  );

  const liveRequiresConsent = user?.requiresRecordingConsent === true;
  const liveStatusUnknown =
    !user ||
    user.requiresRecordingConsent == null ||
    (liveRequiresConsent && !user.recordingConsentText);

  // When the live status is unknown (query failed, offline, unusable payload)
  // fall back to the last-known status cached for the CURRENT user. This is
  // cached server truth — written only on successful current-user resolutions
  // (see use-current-user) — not a client-side simulation of consent state;
  // live data always wins over it when present. With no usable cache either,
  // the gate stays fail-closed below (consentStatusUnknown keeps loading on).
  const cachedStatus = liveStatusUnknown
    ? readCachedConsentStatus(userUuid)
    : null;

  const needsConsent = liveStatusUnknown
    ? cachedStatus?.requiresRecordingConsent === true
    : liveRequiresConsent;
  const consentHeader = liveStatusUnknown
    ? (cachedStatus?.consentHeader ?? null)
    : (user.recordingConsentHeader ?? null);
  const consentText = liveStatusUnknown
    ? (cachedStatus?.consentText ?? null)
    : (user.recordingConsentText ?? null);
  const consentStatusUnknown = liveStatusUnknown && !cachedStatus;

  const logRecordingConsent = useCallback(
    async (action, surface, eventName, noteUuid) => {
      let data;
      try {
        ({ data } = await logConsent({
          variables: {
            action,
            source: "WEB",
            surface,
            ...(noteUuid && { noteUuid }),
          },
        }));
      } catch (error) {
        // Apollo Client 4 no longer exposes error.networkError: GraphQL-layer
        // failures surface as CombinedGraphQLErrors; anything else came from
        // the transport (offline, DNS, 5xx). Tag transport failures so callers
        // can tell "offline" (deferrable, see Record.jsx) apart from a server
        // rejection, which must always fail closed.
        if (
          error instanceof Error &&
          !CombinedGraphQLErrors.is(error)
        ) {
          error.networkError = error;
        }
        throw error;
      }
      const consentEventUuid =
        data?.logRecordingConsent?.consentEventUuid;

      if (!consentEventUuid) {
        throw new Error("Recording consent event was not created.");
      }

      mixpanel.track(eventName, {
        orgUuid:
          user?.organization?.uuid ?? currentTeam?.orgUuid ?? null,
      });

      return consentEventUuid;
    },
    [currentTeam?.orgUuid, logConsent, user?.organization?.uuid],
  );

  const recordConsent = useCallback(
    (surface, noteUuid) =>
      logRecordingConsent(
        "ACKNOWLEDGED",
        surface,
        "Recording Consent Acknowledged",
        noteUuid,
      ),
    [logRecordingConsent],
  );

  const declineConsent = useCallback(
    (surface, noteUuid) =>
      logRecordingConsent(
        "DECLINED",
        surface,
        "Recording Consent Declined",
        noteUuid,
      ),
    [logRecordingConsent],
  );

  return {
    needsConsent,
    recordConsent,
    declineConsent,
    consentHeader,
    consentText,
    loading: userLoading || consentStatusUnknown || isLoggingConsent,
  };
}
